import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

export default function AdminEmail() {
  const [activeTab, setActiveTab] = useState("compose");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, totalPages: 1 });

  // Email compose form
  const [emailForm, setEmailForm] = useState({
    subject: "",
    body: "",
    isHtml: true
  });

  // Statement form
  const [statementForm, setStatementForm] = useState({
    startDate: "",
    endDate: "",
    sendToRole: ""
  });

  // Monthly statement settings form
  const [settingsForm, setSettingsForm] = useState({
    isEnabled: true,
    dayOfMonth: 1,
    hourOfDay: 8,
    sendToUsers: true,
    sendToAgents: true,
    sendToAdmins: false,
    onlyActiveAccounts: true,
    minimumBalanceToSend: 0,
    minimumTransactionsToSend: 0
  });

  const [sendMode, setSendMode] = useState("selected"); // selected, role, all
  const [targetRole, setTargetRole] = useState("User");
  const [message, setMessage] = useState({ type: "", text: "" });

  const token = localStorage.getItem("token");
  
  /*  //FOR RENDER(OLD)
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });
*/

 /* FOR LAPTOP
 const api = axios.create({
  baseURL: "http://localhost:5025/api",
  headers: { Authorization: `Bearer ${token}` }
});
 */
 //ON RENDER 
 const api = axios.create({
  baseURL: "/api",
  headers: { Authorization: `Bearer ${token}` }
});
 
   // Load users for selection
  const loadUsers = useCallback(async () => {
    try {
      const params = {};
      if (roleFilter) params.role = roleFilter;
      if (searchTerm) params.search = searchTerm;
      
      const res = await api.get("/AdminEmail/users", { params });
      setUsers(res.data);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  }, [roleFilter, searchTerm]);

  // Load email logs
  const loadEmailLogs = async (page = 1) => {
    try {
      const res = await api.get("/AdminEmail/logs", {
        params: { page, pageSize: pagination.pageSize }
      });
      setEmailLogs(res.data.items);
      setPagination({
        ...pagination,
        page: res.data.page,
        totalPages: res.data.totalPages
      });
    } catch (err) {
      console.error("Failed to load email logs:", err);
    }
  };

  // Load email stats
  const loadEmailStats = async () => {
    try {
      const res = await api.get("/AdminEmail/stats");
      setEmailStats(res.data);
    } catch (err) {
      console.error("Failed to load email stats:", err);
    }
  };

  // Load monthly statement settings
  const loadSettings = async () => {
    try {
      const res = await api.get("/AdminEmail/monthly-statement-settings");
      setSettings(res.data);
      setSettingsForm(res.data);
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  };

  useEffect(() => {
    loadUsers();
    loadEmailStats();
    loadSettings();
    
    // Initialize statement dates (last month)
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    start.setDate(1);
    end.setDate(0); // Last day of previous month
    
    setStatementForm({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      sendToRole: ""
    });
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (activeTab === "logs") {
      loadEmailLogs();
    }
  }, [activeTab]);

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select all visible users
  const selectAllUsers = () => {
    const allIds = users.map(u => u.id);
    setSelectedUsers(allIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedUsers([]);
  };

  // Send email
  const sendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) {
      setMessage({ type: "error", text: "Please enter subject and message body" });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      let endpoint = "";
      let payload = {
        subject: emailForm.subject,
        body: emailForm.body,
        isHtml: emailForm.isHtml
      };

      switch (sendMode) {
        case "selected":
          if (selectedUsers.length === 0) {
            setMessage({ type: "error", text: "Please select at least one recipient" });
            setLoading(false);
            return;
          }
          if (selectedUsers.length === 1) {
            endpoint = "/AdminEmail/send-single";
            payload.userId = selectedUsers[0];
          } else {
            endpoint = "/AdminEmail/send-bulk";
            payload.userIds = selectedUsers;
          }
          break;
        case "role":
          endpoint = "/AdminEmail/send-to-role";
          payload.role = targetRole;
          break;
        case "all":
          endpoint = "/AdminEmail/send-to-all";
          break;
        default:
          return;
      }

      const res = await api.post(endpoint, payload);
      
      setMessage({ 
        type: "success", 
        text: res.data.message || "Email sent successfully!"
      });
      
      // Reset form
      setEmailForm({ subject: "", body: "", isHtml: true });
      setSelectedUsers([]);
      
      // Refresh stats
      loadEmailStats();
    } catch (err) {
      setMessage({ 
        type: "error", 
        text: err.response?.data?.message || "Failed to send email" 
      });
    } finally {
      setLoading(false);
    }
  };

  // Send statement
  const sendStatement = async (mode) => {
    if (!statementForm.startDate || !statementForm.endDate) {
      setMessage({ type: "error", text: "Please select date range" });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      let endpoint = "";
      let payload = {
        startDate: statementForm.startDate,
        endDate: statementForm.endDate
      };

      switch (mode) {
        case "selected":
          if (selectedUsers.length === 0) {
            setMessage({ type: "error", text: "Please select at least one recipient" });
            setLoading(false);
            return;
          }
          if (selectedUsers.length === 1) {
            endpoint = "/AdminEmail/send-statement";
            payload.userId = selectedUsers[0];
          } else {
            endpoint = "/AdminEmail/send-statements-bulk";
            payload.userIds = selectedUsers;
          }
          break;
        case "role":
          if (!statementForm.sendToRole) {
            setMessage({ type: "error", text: "Please select a role" });
            setLoading(false);
            return;
          }
          endpoint = "/AdminEmail/send-statements-to-role";
          payload.role = statementForm.sendToRole;
          break;
        default:
          return;
      }

      const res = await api.post(endpoint, payload);
      
      setMessage({ 
        type: "success", 
        text: res.data.message || "Statement(s) sent successfully!"
      });
      
      setSelectedUsers([]);
      loadEmailStats();
    } catch (err) {
      setMessage({ 
        type: "error", 
        text: err.response?.data?.message || "Failed to send statement" 
      });
    } finally {
      setLoading(false);
    }
  };

  // Send monthly statements
  const sendMonthlyStatements = async () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    
    if (!window.confirm(`Send monthly statements for ${lastMonth.toLocaleString('default', { month: 'long', year: 'numeric' })} to all eligible users?`)) {
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const res = await api.post("/AdminEmail/send-monthly-statements", {
        year: lastMonth.getFullYear(),
        month: lastMonth.getMonth() + 1
      });
      
      setMessage({ 
        type: "success", 
        text: res.data.message || "Monthly statements sent!"
      });
      
      loadEmailStats();
    } catch (err) {
      setMessage({ 
        type: "error", 
        text: err.response?.data?.message || "Failed to send monthly statements" 
      });
    } finally {
      setLoading(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      await api.post("/AdminEmail/monthly-statement-settings", settingsForm);
      setMessage({ type: "success", text: "Settings saved successfully!" });
      loadSettings();
    } catch (err) {
      setMessage({ 
        type: "error", 
        text: err.response?.data?.message || "Failed to save settings" 
      });
    } finally {
      setLoading(false);
    }
  };

  // Email templates
  const emailTemplates = [
    {
      name: "Welcome Message",
      subject: "Welcome to Uptech Banking!",
      body: `<h2>Welcome to Uptech Banking!</h2>
<p>Dear Customer,</p>
<p>We're thrilled to have you as part of our banking family. Your account is now active and ready to use.</p>
<p>Here's what you can do:</p>
<ul>
<li>Check your balance anytime</li>
<li>Transfer funds instantly</li>
<li>Pay bills conveniently</li>
<li>Track all your transactions</li>
</ul>
<p>If you have any questions, our support team is here to help!</p>
<p>Best regards,<br>Uptech Banking Team</p>`
    },
    {
      name: "System Maintenance",
      subject: "Scheduled System Maintenance Notice",
      body: `<h2>Scheduled Maintenance Notice</h2>
<p>Dear Valued Customer,</p>
<p>We will be performing scheduled maintenance on our systems on <strong>[DATE]</strong> from <strong>[TIME]</strong> to <strong>[TIME]</strong>.</p>
<p>During this period, you may experience intermittent access to our services.</p>
<p>We apologize for any inconvenience and appreciate your patience.</p>
<p>Best regards,<br>Uptech Banking Team</p>`
    },
    {
      name: "Security Alert",
      subject: "Important Security Update",
      body: `<h2>Security Update</h2>
<p>Dear Customer,</p>
<p>As part of our commitment to keeping your account secure, we would like to remind you of some important security practices:</p>
<ul>
<li>Never share your PIN or password with anyone</li>
<li>Always log out after using online banking</li>
<li>Report any suspicious activity immediately</li>
<li>Keep your contact information up to date</li>
</ul>
<p>Your security is our priority.</p>
<p>Best regards,<br>Uptech Banking Security Team</p>`
    },
    {
      name: "Promotion/Offer",
      subject: "Exclusive Offer Just for You!",
      body: `<h2>Special Offer!</h2>
<p>Dear Valued Customer,</p>
<p>We have an exclusive offer just for you!</p>
<p><strong>[OFFER DETAILS HERE]</strong></p>
<p>This offer is valid until <strong>[DATE]</strong>.</p>
<p>Don't miss out on this opportunity!</p>
<p>Best regards,<br>Uptech Banking Team</p>`
    }
  ];

  const applyTemplate = (template) => {
    setEmailForm({
      ...emailForm,
      subject: template.subject,
      body: template.body
    });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📧 Email Management</h1>
        <p style={styles.subtitle}>Send emails and statements to users, agents, and customers</p>
      </div>

      {/* Stats Cards */}
      {emailStats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📤</div>
            <div style={styles.statInfo}>
              <div style={styles.statValue}>{emailStats.totalSent}</div>
              <div style={styles.statLabel}>Total Sent</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>✅</div>
            <div style={styles.statInfo}>
              <div style={styles.statValue}>{emailStats.successfulSent}</div>
              <div style={styles.statLabel}>Successful</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>❌</div>
            <div style={styles.statInfo}>
              <div style={styles.statValue}>{emailStats.failedSent}</div>
              <div style={styles.statLabel}>Failed</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📄</div>
            <div style={styles.statInfo}>
              <div style={styles.statValue}>{emailStats.statementsSent}</div>
              <div style={styles.statLabel}>Statements</div>
            </div>
          </div>
        </div>
      )}

      {/* Message Alert */}
      {message.text && (
        <div style={{
          ...styles.alert,
          backgroundColor: message.type === "success" ? "#d4edda" : "#f8d7da",
          color: message.type === "success" ? "#155724" : "#721c24"
        }}>
          {message.type === "success" ? "✅" : "⚠️"} {message.text}
          <button 
            onClick={() => setMessage({ type: "", text: "" })}
            style={styles.alertClose}
          >×</button>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { id: "compose", label: "✉️ Compose Email" },
          { id: "statements", label: "📄 Send Statements" },
          { id: "logs", label: "📋 Email Logs" },
          { id: "settings", label: "⚙️ Settings" }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {})
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Compose Email Tab */}
      {activeTab === "compose" && (
        <div style={styles.content}>
          <div style={styles.grid}>
            {/* Left: Recipients */}
            <div style={styles.recipientsPanel}>
              <h3 style={styles.panelTitle}>Recipients</h3>
              
              {/* Send Mode Selection */}
              <div style={styles.sendModeSection}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    checked={sendMode === "selected"}
                    onChange={() => setSendMode("selected")}
                  />
                  <span>Selected Users ({selectedUsers.length})</span>
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    checked={sendMode === "role"}
                    onChange={() => setSendMode("role")}
                  />
                  <span>By Role</span>
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    checked={sendMode === "all"}
                    onChange={() => setSendMode("all")}
                  />
                  <span>All Active Users</span>
                </label>
              </div>

              {/* Role Selection (when sendMode === "role") */}
              {sendMode === "role" && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Select Role</label>
                  <select
                    value={targetRole}
                    onChange={(e) => setTargetRole(e.target.value)}
                    style={styles.select}
                  >
                    <option value="User">All Users (Customers)</option>
                    <option value="Agent">All Agents</option>
                    <option value="Admin">All Admins</option>
                  </select>
                </div>
              )}

              {/* User Selection (when sendMode === "selected") */}
              {sendMode === "selected" && (
                <>
                  {/* Search & Filter */}
                  <div style={styles.filterRow}>
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={styles.searchInput}
                    />
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      style={styles.filterSelect}
                    >
                      <option value="">All Roles</option>
                      <option value="User">Users</option>
                      <option value="Agent">Agents</option>
                      <option value="Admin">Admins</option>
                    </select>
                  </div>

                  {/* Selection Actions */}
                  <div style={styles.selectionActions}>
                    <button onClick={selectAllUsers} style={styles.smallBtn}>
                      Select All
                    </button>
                    <button onClick={clearSelection} style={styles.smallBtn}>
                      Clear
                    </button>
                    <span style={styles.selectedCount}>
                      {selectedUsers.length} selected
                    </span>
                  </div>

                  {/* User List */}
                  <div style={styles.userList}>
                    {users.map(user => (
                      <div
                        key={user.id}
                        onClick={() => toggleUserSelection(user.id)}
                        style={{
                          ...styles.userItem,
                          ...(selectedUsers.includes(user.id) ? styles.userItemSelected : {})
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          style={styles.checkbox}
                        />
                        <div style={styles.userInfo}>
                          <div style={styles.userName}>{user.fullName}</div>
                          <div style={styles.userEmail}>{user.email}</div>
                        </div>
                        <span style={{
                          ...styles.roleBadge,
                          backgroundColor: user.role === "Admin" ? "#dc3545" : 
                                          user.role === "Agent" ? "#28a745" : "#007bff"
                        }}>
                          {user.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Right: Compose */}
            <div style={styles.composePanel}>
              <h3 style={styles.panelTitle}>Compose Message</h3>

              {/* Templates */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Quick Templates</label>
                <div style={styles.templateButtons}>
                  {emailTemplates.map((template, idx) => (
                    <button
                      key={idx}
                      onClick={() => applyTemplate(template)}
                      style={styles.templateBtn}
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Subject *</label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="Enter email subject"
                  style={styles.input}
                />
              </div>

              {/* Body */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Message Body *</label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                  placeholder="Enter your message (HTML supported)"
                  style={styles.textarea}
                  rows={12}
                />
              </div>

              {/* HTML Toggle */}
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={emailForm.isHtml}
                    onChange={(e) => setEmailForm({ ...emailForm, isHtml: e.target.checked })}
                  />
                  <span>Send as HTML email</span>
                </label>
              </div>

              {/* Send Button */}
              <button
                onClick={sendEmail}
                disabled={loading}
                style={{
                  ...styles.sendBtn,
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? "⏳ Sending..." : "📤 Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Statements Tab */}
      {activeTab === "statements" && (
        <div style={styles.content}>
          <div style={styles.grid}>
            {/* Left: Recipients (reuse from compose) */}
            <div style={styles.recipientsPanel}>
              <h3 style={styles.panelTitle}>Select Recipients</h3>
              
              {/* Search & Filter */}
              <div style={styles.filterRow}>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={styles.searchInput}
                />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="">All Roles</option>
                  <option value="User">Users</option>
                  <option value="Agent">Agents</option>
                  <option value="Admin">Admins</option>
                </select>
              </div>

              {/* Selection Actions */}
              <div style={styles.selectionActions}>
                <button onClick={selectAllUsers} style={styles.smallBtn}>
                  Select All
                </button>
                <button onClick={clearSelection} style={styles.smallBtn}>
                  Clear
                </button>
                <span style={styles.selectedCount}>
                  {selectedUsers.length} selected
                </span>
              </div>

              {/* User List */}
              <div style={styles.userList}>
                {users.map(user => (
                  <div
                    key={user.id}
                    onClick={() => toggleUserSelection(user.id)}
                    style={{
                      ...styles.userItem,
                      ...(selectedUsers.includes(user.id) ? styles.userItemSelected : {})
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      style={styles.checkbox}
                    />
                    <div style={styles.userInfo}>
                      <div style={styles.userName}>{user.fullName}</div>
                      <div style={styles.userEmail}>{user.email}</div>
                    </div>
                    <span style={{
                      ...styles.roleBadge,
                      backgroundColor: user.role === "Admin" ? "#dc3545" : 
                                      user.role === "Agent" ? "#28a745" : "#007bff"
                    }}>
                      {user.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Statement Options */}
            <div style={styles.composePanel}>
              <h3 style={styles.panelTitle}>Statement Options</h3>

              {/* Date Range */}
              <div style={styles.dateRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Start Date</label>
                  <input
                    type="date"
                    value={statementForm.startDate}
                    onChange={(e) => setStatementForm({ ...statementForm, startDate: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>End Date</label>
                  <input
                    type="date"
                    value={statementForm.endDate}
                    onChange={(e) => setStatementForm({ ...statementForm, endDate: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Quick Select */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Quick Select Period</label>
                <div style={styles.quickSelectBtns}>
                  {[
                    { label: "Last Month", getRange: () => {
                      const end = new Date();
                      end.setDate(0);
                      const start = new Date(end.getFullYear(), end.getMonth(), 1);
                      return { start, end };
                    }},
                    { label: "Last 3 Months", getRange: () => {
                      const end = new Date();
                      const start = new Date();
                      start.setMonth(start.getMonth() - 3);
                      return { start, end };
                    }},
                    { label: "Last 6 Months", getRange: () => {
                      const end = new Date();
                      const start = new Date();
                      start.setMonth(start.getMonth() - 6);
                      return { start, end };
                    }},
                    { label: "This Year", getRange: () => {
                      const end = new Date();
                      const start = new Date(end.getFullYear(), 0, 1);
                      return { start, end };
                    }}
                  ].map((period, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        const { start, end } = period.getRange();
                        setStatementForm({
                          ...statementForm,
                          startDate: start.toISOString().split('T')[0],
                          endDate: end.toISOString().split('T')[0]
                        });
                      }}
                      style={styles.quickBtn}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Send to Selected */}
              <div style={styles.actionSection}>
                <h4 style={styles.actionTitle}>Send to Selected Users</h4>
                <p style={styles.actionDesc}>
                  Send statements to {selectedUsers.length} selected user(s)
                </p>
                <button
                  onClick={() => sendStatement("selected")}
                  disabled={loading || selectedUsers.length === 0}
                  style={{
                    ...styles.actionBtn,
                    backgroundColor: "#007bff",
                    opacity: loading || selectedUsers.length === 0 ? 0.6 : 1
                  }}
                >
                  {loading ? "⏳ Sending..." : "📄 Send to Selected"}
                </button>
              </div>

              {/* Send to Role */}
              <div style={styles.actionSection}>
                <h4 style={styles.actionTitle}>Send to All Users by Role</h4>
                <div style={styles.roleSelectRow}>
                  <select
                    value={statementForm.sendToRole}
                    onChange={(e) => setStatementForm({ ...statementForm, sendToRole: e.target.value })}
                    style={styles.select}
                  >
                    <option value="">Select Role</option>
                    <option value="User">All Users (Customers)</option>
                    <option value="Agent">All Agents</option>
                    <option value="Admin">All Admins</option>
                  </select>
                  <button
                    onClick={() => sendStatement("role")}
                    disabled={loading || !statementForm.sendToRole}
                    style={{
                      ...styles.actionBtn,
                      backgroundColor: "#28a745",
                      opacity: loading || !statementForm.sendToRole ? 0.6 : 1
                    }}
                  >
                    {loading ? "⏳ Sending..." : "📄 Send to Role"}
                  </button>
                </div>
              </div>

              {/* Monthly Statements */}
              <div style={styles.actionSection}>
                <h4 style={styles.actionTitle}>Monthly Statements</h4>
                <p style={styles.actionDesc}>
                  Send last month's statement to all eligible users based on settings
                </p>
                <button
                  onClick={sendMonthlyStatements}
                  disabled={loading}
                  style={{
                    ...styles.actionBtn,
                    backgroundColor: "#6f42c1",
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? "⏳ Sending..." : "📅 Send Monthly Statements"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Logs Tab */}
      {activeTab === "logs" && (
        <div style={styles.content}>
          <div style={styles.logsCard}>
            <h3 style={styles.panelTitle}>Email History</h3>
            
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Recipient</th>
                    <th style={styles.th}>Subject</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.map(log => (
                    <tr key={log.id}>
                      <td style={styles.td}>
                        {new Date(log.sentAt).toLocaleString()}
                      </td>
                      <td style={styles.td}>
                        <div>{log.recipientName}</div>
                        <div style={styles.emailSmall}>{log.recipientEmail}</div>
                      </td>
                      <td style={styles.td}>{log.subject}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.typeBadge,
                          backgroundColor: log.emailType === "Statement" ? "#6f42c1" : 
                                          log.emailType === "Bulk" ? "#17a2b8" : "#6c757d"
                        }}>
                          {log.emailType}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.statusBadge,
                          backgroundColor: log.success ? "#28a745" : "#dc3545"
                        }}>
                          {log.success ? "✓ Sent" : "✗ Failed"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={styles.pagination}>
              <button
                onClick={() => loadEmailLogs(pagination.page - 1)}
                disabled={pagination.page <= 1}
                style={styles.pageBtn}
              >
                ← Previous
              </button>
              <span style={styles.pageInfo}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => loadEmailLogs(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                style={styles.pageBtn}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div style={styles.content}>
          <div style={styles.settingsCard}>
            <h3 style={styles.panelTitle}>Monthly Statement Settings</h3>
            <p style={styles.settingsDesc}>
              Configure automated monthly statement distribution
            </p>

            {/* Enable/Disable */}
            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settingsForm.isEnabled}
                  onChange={(e) => setSettingsForm({ ...settingsForm, isEnabled: e.target.checked })}
                />
                <span>Enable automated monthly statements</span>
              </label>
            </div>

            {/* Schedule */}
            <div style={styles.scheduleRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Day of Month (1-28)</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={settingsForm.dayOfMonth}
                  onChange={(e) => setSettingsForm({ ...settingsForm, dayOfMonth: parseInt(e.target.value) || 1 })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Hour of Day (0-23, UTC)</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={settingsForm.hourOfDay}
                  onChange={(e) => setSettingsForm({ ...settingsForm, hourOfDay: parseInt(e.target.value) || 8 })}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Recipients */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Send To</label>
              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={settingsForm.sendToUsers}
                    onChange={(e) => setSettingsForm({ ...settingsForm, sendToUsers: e.target.checked })}
                  />
                  <span>Users (Customers)</span>
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={settingsForm.sendToAgents}
                    onChange={(e) => setSettingsForm({ ...settingsForm, sendToAgents: e.target.checked })}
                  />
                  <span>Agents</span>
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={settingsForm.sendToAdmins}
                    onChange={(e) => setSettingsForm({ ...settingsForm, sendToAdmins: e.target.checked })}
                  />
                  <span>Admins</span>
                </label>
              </div>
            </div>

            {/* Filters */}
            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settingsForm.onlyActiveAccounts}
                  onChange={(e) => setSettingsForm({ ...settingsForm, onlyActiveAccounts: e.target.checked })}
                />
                <span>Only send to active accounts</span>
              </label>
            </div>

            <div style={styles.scheduleRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Minimum Balance (₦)</label>
                <input
                  type="number"
                  min="0"
                  value={settingsForm.minimumBalanceToSend}
                  onChange={(e) => setSettingsForm({ ...settingsForm, minimumBalanceToSend: parseFloat(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Minimum Transactions</label>
                <input
                  type="number"
                  min="0"
                  value={settingsForm.minimumTransactionsToSend}
                  onChange={(e) => setSettingsForm({ ...settingsForm, minimumTransactionsToSend: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={saveSettings}
              disabled={loading}
              style={{
                ...styles.sendBtn,
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? "⏳ Saving..." : "💾 Save Settings"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: "20px", maxWidth: "1400px", margin: "0 auto" },
  header: { marginBottom: "20px" },
  title: { margin: "0 0 5px", fontSize: "28px", color: "#1a1a2e" },
  subtitle: { margin: 0, color: "#666", fontSize: "14px" },

  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "15px", marginBottom: "20px" },
  statCard: { backgroundColor: "white", borderRadius: "12px", padding: "20px", display: "flex", alignItems: "center", gap: "15px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  statIcon: { fontSize: "28px", width: "50px", height: "50px", backgroundColor: "#f0f2f5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" },
  statInfo: {},
  statValue: { fontSize: "24px", fontWeight: "700", color: "#1a1a2e" },
  statLabel: { fontSize: "12px", color: "#666" },

  alert: { padding: "12px 40px 12px 15px", borderRadius: "8px", marginBottom: "20px", position: "relative" },
  alertClose: { position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", fontSize: "20px", cursor: "pointer", opacity: 0.6 },

  tabs: { display: "flex", gap: "5px", marginBottom: "20px", borderBottom: "1px solid #e9ecef", paddingBottom: "10px" },
  tab: { padding: "10px 20px", border: "none", backgroundColor: "#f0f2f5", borderRadius: "8px 8px 0 0", cursor: "pointer", fontSize: "14px", color: "#666" },
  tabActive: { backgroundColor: "#1a1a2e", color: "white" },

  content: {},
  grid: { display: "grid", gridTemplateColumns: "350px 1fr", gap: "20px" },

  recipientsPanel: { backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", height: "fit-content", maxHeight: "700px", display: "flex", flexDirection: "column" },
  composePanel: { backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  panelTitle: { margin: "0 0 15px", fontSize: "16px", fontWeight: "600", color: "#1a1a2e" },

  sendModeSection: { display: "flex", flexDirection: "column", gap: "10px", marginBottom: "15px", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "8px" },
  radioLabel: { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" },

  filterRow: { display: "flex", gap: "10px", marginBottom: "10px" },
  searchInput: { flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "13px" },
  filterSelect: { padding: "8px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "13px" },

  selectionActions: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" },
  smallBtn: { padding: "5px 10px", border: "1px solid #ddd", backgroundColor: "white", borderRadius: "5px", cursor: "pointer", fontSize: "12px" },
  selectedCount: { fontSize: "12px", color: "#666" },

  userList: { flex: 1, overflowY: "auto", maxHeight: "400px", border: "1px solid #e9ecef", borderRadius: "8px" },
  userItem: { display: "flex", alignItems: "center", gap: "10px", padding: "10px", borderBottom: "1px solid #f0f0f0", cursor: "pointer" },
  userItemSelected: { backgroundColor: "#e3f2fd" },
  checkbox: { cursor: "pointer" },
  userInfo: { flex: 1 },
  userName: { fontSize: "14px", fontWeight: "500" },
  userEmail: { fontSize: "12px", color: "#666" },
  roleBadge: { padding: "3px 8px", borderRadius: "10px", fontSize: "10px", color: "white", fontWeight: "500" },

  formGroup: { marginBottom: "15px" },
  label: { display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: "500", color: "#333" },
  input: { width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  select: { width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", fontFamily: "monospace", resize: "vertical", boxSizing: "border-box" },

  templateButtons: { display: "flex", flexWrap: "wrap", gap: "8px" },
  templateBtn: { padding: "6px 12px", border: "1px solid #007bff", backgroundColor: "white", color: "#007bff", borderRadius: "15px", cursor: "pointer", fontSize: "12px" },

  checkboxLabel: { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" },
  checkboxGroup: { display: "flex", gap: "20px", flexWrap: "wrap" },

  sendBtn: { width: "100%", padding: "14px", backgroundColor: "#1a1a2e", color: "white", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "600", cursor: "pointer" },

  dateRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" },
  quickSelectBtns: { display: "flex", flexWrap: "wrap", gap: "8px" },
  quickBtn: { padding: "6px 12px", border: "1px solid #ddd", backgroundColor: "#f8f9fa", borderRadius: "15px", cursor: "pointer", fontSize: "12px" },

  actionSection: { padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", marginBottom: "15px" },
  actionTitle: { margin: "0 0 5px", fontSize: "14px", fontWeight: "600" },
  actionDesc: { margin: "0 0 10px", fontSize: "12px", color: "#666" },
  actionBtn: { padding: "10px 20px", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "500" },
  roleSelectRow: { display: "flex", gap: "10px" },

  logsCard: { backgroundColor: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  tableWrapper: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "12px 10px", borderBottom: "2px solid #e9ecef", fontSize: "12px", fontWeight: "600", color: "#666", textTransform: "uppercase" },
  td: { padding: "12px 10px", borderBottom: "1px solid #f0f0f0", fontSize: "13px" },
  emailSmall: { fontSize: "11px", color: "#999" },
  typeBadge: { padding: "3px 8px", borderRadius: "10px", fontSize: "10px", color: "white" },
  statusBadge: { padding: "3px 8px", borderRadius: "10px", fontSize: "10px", color: "white" },

  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: "15px", marginTop: "20px" },
  pageBtn: { padding: "8px 15px", border: "1px solid #ddd", backgroundColor: "white", borderRadius: "5px", cursor: "pointer", fontSize: "13px" },
  pageInfo: { fontSize: "13px", color: "#666" },

  settingsCard: { backgroundColor: "white", borderRadius: "12px", padding: "30px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", maxWidth: "600px" },
  settingsDesc: { color: "#666", fontSize: "14px", marginBottom: "20px" },
  scheduleRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }
};
